import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Loader2, Zap, Hand } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuickAddPartDialog = ({ 
  open, 
  onOpenChange, 
  eventId,
  onSuccess 
}) => {
  const [partName, setPartName] = useState('');
  const [partMode, setPartMode] = useState('auto');
  const [duration, setDuration] = useState(30);
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!partName.trim()) {
      toast.error('Veuillez entrer un nom pour la partie');
      return;
    }

    setCreating(true);
    console.log(`➕ Creating new part: ${partName} (${partMode} mode)`);

    try {
      const token = localStorage.getItem('token');
      
      const partResponse = await axios.post(
        `${BACKEND_URL}/api/parts`,
        {
          event_id: eventId,
          name: partName,
          mode: partMode,
          estimated_duration_minutes: duration,
          order_index: 999
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const newPart = partResponse.data;
      console.log(`✅ Part created:`, newPart);

      console.log(`📋 Creating playlists for part: ${newPart.id}`);

      const mainPlaylistResponse = await axios.post(
        `${BACKEND_URL}/api/playlists`,
        {
          part_id: newPart.id,
          name: `${partName} - Principal`,
          type: 'main'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const backupPlaylistResponse = await axios.post(
        `${BACKEND_URL}/api/playlists`,
        {
          part_id: newPart.id,
          name: `${partName} - Secours`,
          type: 'backup'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log(`✅ Playlists created for part ${newPart.id}`);

      toast.success(`🎉 Partie "${partName}" créée avec succès !`);

      if (onSuccess) {
        onSuccess({
          ...newPart,
          playlists: [mainPlaylistResponse.data, backupPlaylistResponse.data]
        });
      }

      setPartName('');
      setPartMode('auto');
      setDuration(30);
      setCreating(false);
      onOpenChange(false);
    } catch (error) {
      console.error('❌ Error creating part:', error);
      toast.error('Erreur lors de la création de la partie');
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-3">
            <Plus className="w-6 h-6 text-violet-600" />
            Ajouter une partie
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Part Name */}
          <div className="space-y-2">
            <Label htmlFor="part-name" className="text-gray-700">
              Nom de la partie
            </Label>
            <Input
              id="part-name"
              value={partName}
              onChange={(e) => setPartName(e.target.value)}
              placeholder="Ex: Fin de soirée"
              className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"
            />
          </div>

          {/* Mode Selection */}
          <div className="space-y-2">
            <Label className="text-gray-700">Mode de lecture</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setPartMode('auto')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  partMode === 'auto'
                    ? 'border-violet-500 bg-violet-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <Zap className="w-6 h-6 mx-auto mb-1 text-violet-600" />
                <p className="font-semibold text-gray-900 text-sm">Auto</p>
                <p className="text-xs text-gray-500 mt-1">
                  Enchaînement automatique
                </p>
              </button>

              <button
                onClick={() => setPartMode('manual')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  partMode === 'manual'
                    ? 'border-violet-500 bg-violet-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <Hand className="w-6 h-6 mx-auto mb-1 text-blue-600" />
                <p className="font-semibold text-gray-900 text-sm">Manuel</p>
                <p className="text-xs text-gray-500 mt-1">
                  Contrôle DJ complet
                </p>
              </button>
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label htmlFor="duration" className="text-gray-700">
              Durée estimée (minutes)
            </Label>
            <Input
              id="duration"
              type="number"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value) || 30)}
              min="5"
              max="300"
              className="bg-gray-50 border-gray-200 text-gray-900"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700">
              ℹ️ La partie sera créée avec une playlist principale et une playlist secours vides. Vous pourrez y ajouter des titres depuis l'éditeur d'événement ou directement ici.
            </p>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={creating}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Annuler
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!partName.trim() || creating}
            className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Création...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Créer la partie
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddPartDialog;
